/**
 * Single source of truth for all language-specific URL path segments.
 *
 * Keys are canonical route identifiers used throughout the app.
 * Values are the actual URL segments served for each language.
 */

export type SupportedLang = 'en' | 'es';

export const PATH_TRANSLATIONS: Record<
  SupportedLang,
  Record<string, string>
> = {
  en: {
    home: 'home',
    overview: 'overview',
    testimonials: 'testimonials',
    updates: 'updates',
    terms: 'terms',
    privacy: 'privacy',
    studentprivacy: 'studentprivacy',
    aboutus: 'aboutus',
    webinar: 'webinar',
    materials: 'materials',
    'gradesk-1': 'gradesk-1',
    'grades2-3': 'grades2-3',
    'grades4-5': 'grades4-5',
    grade6: 'grade6',
    'grades7-8': 'grades7-8',
    'grades9-10': 'grades9-10',
    'grades11-12': 'grades11-12',
    'order-materials': 'order-materials',
    'home-v2': 'home-v2',
    digital: 'digital',
    invest: 'invest',
    register: 'register',
    login: 'login',
    'learning-library': 'learning-library',
    nic: 'national-investment-challenge',
  },
  es: {
    home: 'inicio',
    overview: 'resumen',
    testimonials: 'testimonios',
    updates: 'actualizaciones',
    terms: 'terminos',
    privacy: 'privacidad',
    studentprivacy: 'privacidadestudiantil',
    aboutus: 'sobre-nosotros',
    webinar: 'webinar',
    materials: 'materiales',
    'gradesk-1': 'gradosk-1',
    'grades2-3': 'grados2-3',
    'grades4-5': 'grados4-5',
    grade6: 'grado6',
    'grades7-8': 'grados7-8',
    'grades9-10': 'grados9-10',
    'grades11-12': 'grados11-12',
    'order-materials': 'pedir-materiales',
    'home-v2': 'inicio-v2',
    digital: 'digital',
    invest: 'invertir',
    register: 'registro',
    login: 'inicio-sesion',
    'learning-library': 'biblioteca-de-aprendizaje',
    nic: 'desafio-nacional-de-inversion',
  },
};

export const PAGE_TITLES: Record<SupportedLang, { invest: string; digital: string; overview: string; learningLibrary: string; nic: string }> = {
  en: {
    invest: 'MyCI Overview',
    digital: 'MyCE Digital Overview',
    overview: 'MyCE Paper Overview',
    learningLibrary: 'MyCI Learning Library',
    nic: 'National Investment Challenge',
  },
  es: {
    invest: 'Descripción de MyCI',
    digital: 'Descripción de MyCE Digital',
    overview: 'Descripción de MyCE Paper',
    learningLibrary: 'Biblioteca de Aprendizaje de MyCI',
    nic: 'Desafío Nacional de Inversión',
  },
};

/**
 * All supported language codes, derived from PATH_TRANSLATIONS so this
 * list never needs to be updated manually when a new language is added.
 */
export const SUPPORTED_LANGS = Object.keys(
  PATH_TRANSLATIONS
) as ReadonlyArray<SupportedLang>;

/**
 * Given a raw language string (e.g. from TranslateService.currentLang),
 * returns the nearest supported language, falling back to 'en'.
 */
export function resolveSupportedLang(lang: string | undefined | null): SupportedLang {
  return SUPPORTED_LANGS.includes(lang as SupportedLang) ? (lang as SupportedLang) : 'en';
}

/**
 * Given a language and a localized URL segment, returns the canonical
 * (English key) for that path. Used by LanguageService when switching
 * languages to map from the current localized URL to the equivalent
 * URL in the new language.
 *
 * Example: toCanonicalPath('es', 'testimonios') → 'testimonials'
 */
export function toCanonicalPath(
  lang: SupportedLang,
  localizedPath: string
): string {
  const entries = Object.entries(PATH_TRANSLATIONS[lang] ?? {});
  return entries.find(([, v]) => v === localizedPath)?.[0] ?? localizedPath;
}
